import { useCallback, useRef } from "react"
import { environment } from "modules/environments"
import { useUser } from "modules/users"
import { useGetData as useGetProspect } from "services/prospect"
import { useGetData, usePostData } from "services/sales"
import { handleResponse } from "utils/fetch"
import { FEDERATION_GLB } from "views/constants/jsonData/globalData"

const hostNameMap = {
  develop: "my.dev.gogoro.cc",
  regression: "my.qa.gogoro.cc",
  staging: "my.gogoro.cc",
  production: "my.gogoro.com",
  globe_develop: "my.globe.dev.gogoro.cc",
  globe_regression: "my.globe.qa.gogoro.cc",
  globe_staging: "my.globe.gogoro.cc",
  globe_production: "my.gogoro.com.ph",
}

export const myGogoroHostName = hostNameMap[environment]

export const GENDER_MALE = 1
export const GENDER_FEMALE = 2
export const GENDER_CORPORATION = 3

export const checkProperties = (customer) => {
  // TODO merge into customer panel
  const essentialData = Object.assign(
    customer.Gender === GENDER_CORPORATION
      ? { FirstName: "公司名稱", IDCard: "統一編號" }
      : { FirstName: "名字", LastName: "姓氏", IDCard: "身份證號", Birthday: "生日" },
    {
      Gender: "性別",
      Mobile: "手機",
      Email: "Email",
      City: "縣市(城市)",
      District: "鄉鎮市區(區域)",
      Address: "地址",
      ZipCode: "郵遞區號",
    },
  )
  const invalidData = Object.keys(essentialData).reduce((acc, cur) => {
    // 表示在my account尚未填寫生日
    if (cur === "Birthday" && customer.Birthday === "0001-01-01T00:00:00") {
      return acc.concat(essentialData[cur])
    }

    if (customer[cur]) {
      return acc
    }
    return acc.concat(essentialData[cur])
  }, [])
  return invalidData
}

export const useCustomers = () => {
  const queryCustomer = useGetData("/tw/api/customer/get-customer")
  const getCustomersReport = useGetProspect("/api/v1/customers/report-query")
  const queryDriver = useGetData(
    useCallback((plate) => `/tw/api/transfer/get-scooter-driver-info?Plate=${plate.toUpperCase()}`, []),
  )

  const queryCustomersReport = useCallback(
    ({ since, until }) => getCustomersReport({ start: since, end: until }),
    [getCustomersReport],
  )

  const queryCustomerByPhone = useCallback(
    (phone) => {
      return queryCustomer({ phone }).then((response) => {
        if (!response) return
        if (response.Result !== 1) throw Error(`${response.MessageCode} ${response.Message}`)
        else {
          return response.Data
        }
      })
    },
    [queryCustomer],
  )

  const queryDriverByPlate = useCallback(
    (plate) => {
      return queryDriver(plate)
        .then(handleResponse)
        .then((response) => response.Data)
    },
    [queryDriver],
  )

  return { queryCustomerByPhone, queryCustomersReport, queryDriverByPlate }
}

export const useCustomer = (customerId) => {
  const { Federation } = useUser()
  const queryData = usePostData("/tw/api/utility/get-dp-user-data")

  // normalize properties for other APIs
  const normalizeCustomer = useCallback(
    (customer) => {
      return Object.assign({}, customer, {
        Email: customer.EMail,
        Name:
          Federation === FEDERATION_GLB
            ? `${customer.FirstName || ""} ${customer.LastName || ""}`
            : `${customer.LastName || ""}${customer.FirstName || ""}`,
      })
    },
    [Federation],
  )

  const queryCustomer = useCallback(
    ({ email, code }) =>
      queryData({ Email: email, Phone: code })
        .then(handleResponse)
        .then((response) => normalizeCustomer(response.Data)),
    [queryData, normalizeCustomer],
  )

  const fetchCustomer = useCallback(
    () =>
      queryData({ UserId: customerId })
        .then(handleResponse)
        .then((response) => normalizeCustomer(response.Data)),
    [queryData, customerId, normalizeCustomer],
  )

  return { queryCustomer, fetchCustomer }
}

export const useTransaction = () => {
  const resultCount = useRef()
  const fetchData = usePostData(`/tw/api/transaction/get-authorized-payments`)
  // const queryPoint = usePostData('/tw/api/transaction/get-issue-point');
  const queryPoint = usePostData("/tw/api/transaction/get-point-record")

  const fetchAuthorizedPayments = useCallback(
    ({ qrcode, amount, ...others }) =>
      fetchData({ QRCode: qrcode, Amount: amount, ...others }).then((response) => response.Data),
    [fetchData],
  )

  const requestPointReport = useCallback(
    (query) => {
      return queryPoint(query).then((response) => {
        if (response.Result !== 1) {
          throw Error(`${response.MessageCode} ${response.Message}`)
        }
        resultCount.current = response.Data.TotalCount
        return response.Data.List
      })
    },
    [queryPoint],
  )

  return { fetchAuthorizedPayments, requestPointReport, resultCount }
}
